import { useQuery } from '@tanstack/react-query'
import ms from 'ms'

import { MyUrlSearchParams } from '@/lib/utils'
import { useSelectedDay } from '@hooks/useSelectedDay'
import { applyFacingGeometryTransformation } from '@state/layout/transformations/facings'
import { applyDayStateTransformation } from '@state/shelf/transformations/dayState'
import { recalculateShelves, recalculateShelvesInFrame } from '@state/shelf/transformations/geometric'
import { shelfDtoToWrapperPipeline } from '@state/shelf/transformations/pipelines'
import useViewContextAwareHttpClient from '@utils/ViewContextAwareHttpClient'
import { useStockLevelMapper } from '@utils/stockLevelMapper.ts'

import { applyLayerGeometryTransformation } from './transformations/layers'

import type { LayoutResponse, TransformedLayout } from './types'
import type { operations } from '@/schema'
import type { FacingWithGeometry, LayerWithGeometry } from '@utils/geometry.ts'

const layoutKeys = {
  all: (storeId: string, viewKey: string, options: UseLayoutOptions) => ['layout', storeId, viewKey, options] as const,
  storeLayout: (storeId: string, viewKey: string, options: UseLayoutOptions) =>
    [...layoutKeys.all(storeId, viewKey, options)] as const,
  storeLayoutTransformed: (storeId: string, viewKey: string, options: UseLayoutOptions) =>
    [...layoutKeys.storeLayout(storeId, viewKey, options), 'transformed'] as const,
  globalizedFacings: (storeId: string, viewKey: string, options: UseLayoutOptions) =>
    [...layoutKeys.all(storeId, viewKey, options), 'facings'] as const,
  globalizedLayers: (storeId: string, viewKey: string, options: UseLayoutOptions) =>
    [...layoutKeys.all(storeId, viewKey, options), 'layers'] as const,
}

export type UseLayoutOptions = NonNullable<operations['layoutWithScanId']['parameters']['query']>

export type MapOrigin = NonNullable<UseLayoutOptions['mapOrigin']>

const LAYOUT_RESPONSE_VERSION = '2'

/** For the readers that don't pick an origin: shelf detail, shelf search, cash registers. They must match the map. */
function useDefaultMapOrigin(): MapOrigin {
  const { dayScan } = useSelectedDay()
  return dayScan ? 'SCAN' : 'DEPLOY_DB'
}

/**
 * React Query hook for fetching raw layout data.
 *
 * @param options - Query options
 * @param options.complete - Whether to fetch complete layout with all details
 * @param query.ignoreDayScan - Read the selected day even while a single scan of it is shown
 * @returns Query result with raw layout data
 */
export function useLayout({ complete = false, mapOrigin: chosenMapOrigin }: UseLayoutOptions = {}, { ignoreDayScan = false, enabled = true } = {}) {
  const { get, storeId, viewKey } = useViewContextAwareHttpClient({ ignoreDayScan })
  const defaultMapOrigin = useDefaultMapOrigin()
  const mapOrigin = chosenMapOrigin ?? defaultMapOrigin

  return useQuery<LayoutResponse>({
    queryKey: layoutKeys.storeLayout(storeId, viewKey, { complete, mapOrigin }),
    queryFn: () => {
      const searchParams = new MyUrlSearchParams({ complete, mapOrigin })

      // Cache buster: the backend sends layouts of older scans as `immutable` for 7 days, so browsers
      // keep using a cached response even after its shape changed. Bump whenever the layout response
      // changes (v2: layers got `priceLabels`).
      searchParams.append('v', LAYOUT_RESPONSE_VERSION)

      return get<LayoutResponse>('/layout' + searchParams.toString())
    },
    enabled,
    // shortcut: a day layout only updates on reload, parts arriving while the map is open don't
    // show; revisit with "refresh while open" (parked in the epic)
    staleTime: ms('1d'),
  })
}

/**
 * React Query hook for fetching and transforming layout data.
 *
 * Applies geometric transformation to convert shelves from 3D world space
 * to normalized 2D screen space.
 *
 * @param options - Query options
 * @param options.complete - Whether to fetch complete layout with all details
 * @returns Query result with transformed layout data
 */
export function useLayoutTransformed({ complete = false, mapOrigin: chosenMapOrigin }: UseLayoutOptions = {}) {
  const { storeId, viewKey } = useViewContextAwareHttpClient()
  const defaultMapOrigin = useDefaultMapOrigin()
  const mapOrigin = chosenMapOrigin ?? defaultMapOrigin
  const layoutQuery = useLayout({ complete, mapOrigin })
  // The scan layout is the orientation authority for every origin: a deploy-db and a scan describe
  // the same store with different shelf sets, and an oriented bounding box is only defined modulo
  // 180°, so deriving the angle per origin lets the map flip when toggling. See recalculateShelves.
  // Deduplicated by React Query when the displayed layout is already the light scan layout.
  const orientationQuery = useLayout({ complete: false, mapOrigin: 'SCAN' })
  // A single scan stays on the selected day's floor, so stepping through scans doesn't move the map.
  // The day's scan map, not the deploy-db: a deploy-db can sit off its scans (BNT, D2K7).
  const { dayScan } = useSelectedDay()
  const dayFloorQuery = useLayout({ complete: false, mapOrigin: 'SCAN' }, { ignoreDayScan: true, enabled: !!dayScan })
  const stockLevelMapper = useStockLevelMapper()

  return useQuery<TransformedLayout>({
    queryKey: layoutKeys.storeLayoutTransformed(storeId, viewKey, { complete, mapOrigin }),
    queryFn: () => {
      const layout = layoutQuery.data!
      const shelves = layout.floors[0]?.shelves ?? []
      const orientationShelves = orientationQuery.data?.floors[0]?.shelves
      const dayFloorShelves = dayScan ? dayFloorQuery.data?.floors[0]?.shelves : undefined

      const fixedLayout = dayFloorShelves?.length
        ? recalculateShelvesInFrame(shelves, recalculateShelves(dayFloorShelves, 'landscape', dayFloorShelves))
        : recalculateShelves(shelves, 'landscape', orientationShelves)
      return {
        ...fixedLayout,
        shelves: fixedLayout.shelves.map(shelf =>
          shelfDtoToWrapperPipeline(applyDayStateTransformation(shelf, layout.day?.asOf), stockLevelMapper)
        ),
      }
    },
    enabled: !!layoutQuery.data && !!orientationQuery.data && (!dayScan || dayFloorQuery.isFetched),
    staleTime: ms('1d'),

    // WARNING: do *NOT* enable placeholderdata as this compromises any assumptions that e.g. the facings calculations are having here
    // placeholderData: keepPreviousData,
  })
}

/**
 * React Query hook for fetching globalized facing geometries.
 *
 * Builds 3D geometry data for all facings in the layout, useful for
 * rendering facings in 3D space or calculating spatial relationships.
 *
 * @returns Query result with map of facing IDs to geometry data
 */
export function useGlobalizedFacings(options: Omit<UseLayoutOptions, 'complete'> = {}) {
  const fullOptions = { complete: true, ...options }
  const { storeId, viewKey } = useViewContextAwareHttpClient()
  const transformedLayoutQuery = useLayoutTransformed(fullOptions)

  return useQuery<Map<string, FacingWithGeometry>>({
    queryKey: layoutKeys.globalizedFacings(storeId, viewKey, fullOptions),
    queryFn: () => {
      if (!transformedLayoutQuery.data) return new Map()
      return applyFacingGeometryTransformation(transformedLayoutQuery.data)
    },
    enabled: !!transformedLayoutQuery.data,
    staleTime: ms('1d'),
  })
}

/**
 * React Query hook for fetching globalized layer geometries.
 *
 * Builds 3D geometry data for all layers in the layout, useful for
 * rendering layers in 3D space or calculating spatial relationships.
 *
 * @returns Query result with map of layer IDs to geometry data
 */
export function useGlobalizedLayers(options: Omit<UseLayoutOptions, 'complete'> = {}) {
  const fullOptions = { complete: true, ...options }
  const { storeId, viewKey } = useViewContextAwareHttpClient()
  const transformedLayoutQuery = useLayoutTransformed(fullOptions)

  return useQuery<Map<string, LayerWithGeometry>>({
    queryKey: layoutKeys.globalizedLayers(storeId, viewKey, fullOptions),
    queryFn: () => {
      if (!transformedLayoutQuery.data) return new Map()
      return applyLayerGeometryTransformation(transformedLayoutQuery.data)
    },
    enabled: !!transformedLayoutQuery.data,
    staleTime: ms('1d'),
  })
}

